// SeatStat — one licensing tier's figure, the NUMBER as the hero: active sites
// over the purchased seat cap, with the headroom beneath (ADR-055 floating
// seats). Promoted out of the platform-projects page folder when the
// project-admin settings surface became its second consumer — both surfaces
// render the same licensing fact, so it can't live in one page's helpers.
//
// Namespace-free by design: the two headroom strings arrive as props, so a
// consumer supplies them from ITS OWN i18n namespace (COMPONENTS §Component
// taxonomy — a composite owns the invariant, the caller owns the variant). The
// invariant here is the number/label hierarchy and the full-vs-headroom rule.
//
// `cap: null` = metered (esco has no seat rows) → the active count alone, no
// denominator and no headroom.

import { Badge } from '../ui/Badge.js';
import { formatNumber } from '../../lib/format.js';

export interface SeatStatProps {
  /** The tier's display name (e.g. "Simple"). */
  label: string;
  /** Active sites currently holding a seat of this tier. */
  active: number;
  /** Purchased seat cap; null = uncapped/metered. */
  cap: number | null;
  /** Shown as a warning Badge when the tier is exhausted (already i18n'd). */
  fullLabel: string;
  /** Remaining-headroom text, e.g. "12 left" (already i18n'd + pluralized). */
  leftLabel: (remaining: number) => string;
  /** Smaller number for a narrow host (the peek). */
  dense?: boolean;
}

export function SeatStat({ label, active, cap, fullLabel, leftLabel, dense = false }: SeatStatProps) {
  const left = cap == null ? null : cap - active;
  return (
    <div className="min-w-[3.5rem]">
      <div className={`tabular-nums font-semibold text-[var(--color-text-primary)] ${dense ? 'text-lg' : 'text-xl'}`}>
        {formatNumber(active)}
        {cap != null && <span className="text-sm font-normal text-[var(--color-text-secondary)]"> / {formatNumber(cap)}</span>}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
        <span>{label}</span>
        {left != null &&
          (left === 0 ? (
            <Badge variant="warning" appearance="subtle">
              {fullLabel}
            </Badge>
          ) : (
            <span>· {leftLabel(left)}</span>
          ))}
      </div>
    </div>
  );
}
